/**
 * E3. Слоты. Три барабана по шесть равновероятных символов:
 * вишня, колокол, подкова, семёрка, пусто, пусто.
 * Выплаты (полный возврат): три семёрки ×50, три одинаковых ×8
 * (в том числе три «пусто»), ровно две семёрки ×4.
 * Доля возврата следует из таблицы: (50 + 8·11 + 4·15) / 216 = 198/216 ≈ 0,917 —
 * это и есть «92 %» из спеки (при «две семёрки ×3» получалось бы лишь 0,847).
 * Значение считается из правил и показывается на экране как есть.
 * Тяга +4 за серию, при автоповторе ×10 — +8. Здоровье −1 (бессонная ночь).
 * Без ставки (денег нет) ни одного прокрута — `noPlay()`.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import type { Life, MinigameOutcome, Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { casinoAvailable, clampBet, expectancy, noPlay } from './common';

export type SlotSymbol = 'cherry' | 'bell' | 'horseshoe' | 'seven' | 'blank';

export const SLOT_REEL: readonly SlotSymbol[] = ['cherry', 'bell', 'horseshoe', 'seven', 'blank', 'blank'];
export const SLOTS_PAYOUT_THREE_SEVENS = 50;
export const SLOTS_PAYOUT_THREE_SAME = 8;
export const SLOTS_PAYOUT_TWO_SEVENS = 4;
export const SLOTS_AUTO_SPINS = 10;
export const SLOTS_URGE = 4;
export const SLOTS_URGE_AUTO = 8;

export interface SlotsState {
  maxBet: number;
}

export interface SlotsInput {
  bet: number;
  auto10?: boolean;
}

export type Reels = [SlotSymbol, SlotSymbol, SlotSymbol];

/** Множитель полного возврата для трёх выпавших символов. */
export function slotsPayout(reels: readonly SlotSymbol[]): number {
  const sevens = reels.filter((s) => s === 'seven').length;
  if (sevens === 3) return SLOTS_PAYOUT_THREE_SEVENS;
  if (reels[0] === reels[1] && reels[1] === reels[2]) return SLOTS_PAYOUT_THREE_SAME;
  if (sevens === 2) return SLOTS_PAYOUT_TWO_SEVENS;
  return 0;
}

/** Теоретическая доля возврата по таблице выплат и равновероятным барабанам. */
export function slotsRtp(): number {
  let total = 0;
  for (const a of SLOT_REEL) for (const b of SLOT_REEL) for (const c of SLOT_REEL) total += slotsPayout([a, b, c]);
  return total / SLOT_REEL.length ** 3;
}

export const SLOTS_RTP = slotsRtp();

export function spinReels(rng: Rng): Reels {
  return [rng.pick(SLOT_REEL), rng.pick(SLOT_REEL), rng.pick(SLOT_REEL)];
}

export const slots: MinigameDef<SlotsInput, SlotsState> = {
  id: 'slots',
  section: 'E',
  triggers: [{ kind: 'envelope' }],
  available(life: Life, _ds: Dataset, law: GamblingLaw): boolean {
    return casinoAvailable(life, law);
  },
  setup(life: Life): SlotsState {
    return { maxBet: Math.max(0, life.money) };
  },
  play(state: SlotsState, input: SlotsInput, life: Life, rng: Rng): MinigameOutcome {
    const spins = input.auto10 ? SLOTS_AUTO_SPINS : 1;
    if (clampBet(Math.min(input.bet, state.maxBet), life.money) <= 0) return noPlay();
    let cash = Math.max(0, life.money);
    let total = 0;
    let last: Reels = ['blank', 'blank', 'blank'];
    for (let i = 0; i < spins; i += 1) {
      const bet = clampBet(Math.min(input.bet, state.maxBet), cash);
      if (bet <= 0) break;
      last = spinReels(rng);
      const delta = bet * slotsPayout(last) - bet;
      cash += delta;
      total += delta;
    }
    return {
      moneyDelta: total,
      stats: { health: -1 },
      urgeDelta: input.auto10 ? SLOTS_URGE_AUTO : SLOTS_URGE,
      logKey: total >= 0 ? 'minigame.slots.win' : 'minigame.slots.lose',
      logParams: { amount: Math.round(Math.abs(total)), spins, reels: last.join(',') },
    };
  },
  expected(): MinigameOutcome {
    return noPlay();
  },
  expectancyText: () => expectancy('slots', SLOTS_RTP),
};
